import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { analyticsApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import KpiCard from './KpiCard'
import { EmptyChart } from './EmptyChart'
import { DotLoader } from '@/components/ui/DotLoader'
import { cardTitleStyle } from '@/lib/styleKit'
import { ChatCircle } from '@phosphor-icons/react'
import {
  AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import { useReducedMotion } from 'framer-motion'
import { useChartTheme, tooltipContentStyle, seriesColor, SERIES_COLORS } from './chartTheme'
import { SENTIMENT_ORDER, sentimentChartColor, sentimentLabel, sentimentMeta } from '@/lib/sentiment'
import type { ChartSentimentCounts, RepliesData, Period } from '@/types/analytics'

interface TabProps {
  period: Period | null
  startDate: string | null
  endDate: string | null
  userId: number | null
  /** One mailbox address, or undefined for the combined view. */
  senderEmail?: string
}

/*
 * Every hue on this tab is imported, none declared: sentiment hues, labels and
 * chip tones come from `lib/sentiment` (§10.1), the lifecycle-metric hues from
 * `chartTheme`'s `SERIES_COLORS` (§61). The timeline, the donut, the KPI row and
 * the Inbox chips are one taxonomy, so this file keeps no colour or label table
 * of its own.
 *
 * That retires both amber accents this file used to own — the amber `neutral`
 * sentiment (now brand indigo, already ruled in `lib/sentiment`) and the amber
 * "Avg response time" card. §61 admits amber as a data hue only as the `clicked`
 * member of `SERIES_COLORS` and as the warning step of `ENGAGEMENT_COLORS`;
 * everywhere else it stays a trust/security signal (§3).
 */

/**
 * Runtime witness for the buckets `sentiment_over_time` actually carries:
 * `ChartSentimentCounts`, i.e. the classifier's vocabulary minus
 * `out_of_office` (the backend filters the query with
 * `sentiment.in_(CHART_SENTIMENTS)`), and with no `unanalyzed` either — a NULL
 * sentiment has no bucket in the trend. Typing it as a `Record` over the
 * interface's keys makes a new bucket in the type a compile error here until it
 * is listed: the exhaustiveness five hand-written `<Area>` elements with
 * hard-coded hex could never give.
 */
const TIMELINE_BUCKETS: Record<keyof ChartSentimentCounts, true> = {
  positive: true,
  neutral: true,
  negative: true,
}

/**
 * The stacked-area series, derived once from the shared order. Stacking order,
 * legend order, label and hue are all downstream of `lib/sentiment`, so none of
 * them can drift away from the Inbox again.
 */
// Intent = what the replier wants, charted on its own axis alongside sentiment
// (how they feel). Deliberately a DIFFERENT hue family from the sentiment tone
// ramp: intent has no good/bad ordering, so reusing the ramp would imply one.
// Ordered most- to least-actionable, with the catch-all in neutral gray.
const INTENT_ORDER = [
  'meeting_request',
  'information_request',
  'interested',
  'referral',
  'objection',
  'not_interested',
  'unsubscribe',
  'other',
  'unanalyzed',
] as const

const INTENT_COLORS: Record<string, string> = {
  meeting_request:     '#5e6ad2', // indigo — the most valuable action
  information_request: '#7170ff', // violet
  interested:          '#8b8ff5', // violet light
  referral:            '#06b6d4', // cyan — routes elsewhere
  objection:           '#f5a524', // amber — engaged but blocked
  not_interested:      '#f87171', // red light
  unsubscribe:         '#ef4444', // red — compliance
  other:               '#8a8f98', // tertiary — real reply, no named bucket
  unanalyzed:          '#62666d', // quaternary
}

const INTENT_LABELS: Record<string, string> = {
  meeting_request:     'Meeting request',
  information_request: 'Information request',
  interested:          'Interested',
  referral:            'Referral',
  objection:           'Objection',
  not_interested:      'Not interested',
  unsubscribe:         'Unsubscribe',
  other:               'None',
  unanalyzed:          'Unknown',
}

export const SENTIMENT_SERIES = SENTIMENT_ORDER.filter(
  (key): key is keyof ChartSentimentCounts => key in TIMELINE_BUCKETS,
).map((key) => ({
  key,
  label: sentimentLabel(key),
  color: sentimentChartColor(key),
  // Namespaced: SVG ids are document-global, so a bare `sentPositive` would
  // collide the day another chart on this page defines its own gradients.
  gradientId: `repliesSentiment-${key}`,
  /**
   * Redundant encoding for the negative-valence bands — a dashed stroke keeps
   * "good news / bad news" legible in greyscale and for a reader with red-green
   * CVD. Read off the shared map's badge variant instead of written per
   * element, so a sentiment the backend adds later as `destructive` is dashed
   * for free, and one added as `success` can never inherit a dash.
   */
  dash: sentimentMeta(key).variant === 'destructive' ? '5 3' : undefined,
}))

/**
 * `meeting_request` → `Meeting request`. Reply intent is a separate backend
 * taxonomy (`EmailLog.reply_intent`) with no shared map yet, so it is humanized
 * locally — but into the same sentence case `sentimentLabel` produces, so the
 * two chips on one row don't read as two conventions. The CSS `capitalize` this
 * replaces rendered "Meeting Request" beside "Not interested", and an
 * all-whitespace intent rendered an empty pill.
 */
function intentLabel(intent: string): string {
  const spaced = intent.replace(/[_-]+/g, ' ').trim().toLowerCase()
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : ''
}

const cardCls = cn(
  'rounded-xl border transition-colors duration-150',
  'bg-card border-border/50',
  'dark:bg-white/[0.02] dark:border-white/[0.08]',
)

export default function RepliesTab({ period, startDate, endDate, userId, senderEmail }: TabProps) {
  const chartTheme = useChartTheme()
  // Recharts animates via react-smooth (JS-driven), so it ignores both the global
  // CSS reduced-motion guard and framer-motion's <MotionConfig>. Gate it explicitly.
  const prefersReduced = useReducedMotion()
  const { data, isLoading, isPreviousData } = useQuery<RepliesData>({
    queryKey: ['analytics', 'replies', period, startDate, endDate, userId, senderEmail],
    queryFn: () =>
      analyticsApi
        .getReplies({ period: period || undefined, start: startDate || undefined, end: endDate || undefined, user_id: userId ?? undefined, sender_email: senderEmail })
        .then((r) => r.data),
    // Reply sentiment/intent is produced asynchronously on the server (mailbox
    // polling + AI analysis), so the client can't know exactly when new data
    // lands. Refetch whenever the user enters the tab or refocuses the window;
    // a short staleTime keeps rapid re-mounts from hammering the API (ADO 21600).
    staleTime: 5 * 1000,
    // Live polling disabled for now to avoid continuous backend load under high
    // traffic. Re-enable if replies must update while the tab stays open with no
    // user interaction (ADO 21838).
    // refetchInterval: 60 * 1000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    // Changing the mailbox (or the period) keeps the charts on screen, dimmed,
    // instead of tearing the whole tab down to a centred spinner and back --
    // the reader loses their place, and on a wide tab the reflow is the most
    // visible thing on the page. Same treatment the Campaigns tab uses.
    keepPreviousData: true,
  })

  // Only a genuine first load reaches the loader; a filter change is carried
  // by keepPreviousData above.
  if (isLoading) {
    // §55: the one loader, announced. `DotLoader` carries role="status" and its
    // own accessible name, and inherits colour through `bg-current`, so the
    // brand hex lives on the wrapper.
    return (
      <div className="flex items-center justify-center py-24 text-[#5e6ad2] dark:text-[#a5b4fc]">
        <DotLoader size="lg" label="Loading reply analytics" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center max-w-md mx-auto">
        <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mb-4">
          <ChatCircle className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />
        </div>
        <h3
          className="text-foreground dark:text-[#f7f8f8] mb-2"
          style={{ fontSize: '20px', fontWeight: 590, letterSpacing: '-0.24px', lineHeight: 1.33 }}
        >
          No reply data
        </h3>
        <p
          className="text-muted-foreground dark:text-[#8a8f98] leading-relaxed"
          style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
        >
          No reply data available for this period.
        </p>
      </div>
    )
  }

  // Slices follow the shared SENTIMENT_ORDER rather than whatever order the API
  // serialised its keys in, so the two grays (out_of_office, unanalyzed) never
  // land next to each other or next to neutral, and every surface lists
  // sentiments in the same sequence. Analytics omits out_of_office from these
  // buckets by design (see types/analytics.ts), hence the presence guard.
  const breakdown = data.sentiment_breakdown
  const sentimentBreakdownData = SENTIMENT_ORDER.filter(
    (key): key is keyof typeof breakdown => key in breakdown,
  ).map((key) => ({
    key,
    name: sentimentLabel(key),
    value: breakdown[key],
    color: sentimentChartColor(key),
  }))
  const sentimentBreakdownTotal = sentimentBreakdownData.reduce((s, d) => s + d.value, 0)
  // Empty buckets never reach the ARC: recharts lays out a zero-value sector
  // anyway and `paddingAngle` then pads it into a visible sliver, so a bucket
  // with no replies painted a stray tick of its own hue onto the ring.
  //
  // They DO stay in the legend, though. Dropping them there made a bucket
  // vanish entirely — "where is Negative?" — and an absent row is ambiguous
  // between "none this period" and "not tracked". An explicit `Negative 0%`
  // answers that. Percentages divide the full total either way, so they remain
  // shares of all replies.
  const sentimentSlices = sentimentBreakdownData.filter((d) => d.value > 0)

  // Fixed INTENT_ORDER rather than Object.entries so the legend and the arc
  // order stay stable as counts shift between periods. Zero-count intents are
  // dropped so the legend doesn't fill with empty categories.
  const intentBreakdown = data.intent_breakdown ?? ({} as Record<string, number>)
  const intentBreakdownData = INTENT_ORDER
    .map((key) => ({
      name: INTENT_LABELS[key] ?? key,
      value: intentBreakdown[key as keyof typeof intentBreakdown] ?? 0,
      color: INTENT_COLORS[key],
    }))
    .filter((d) => d.value > 0)
  const intentBreakdownTotal = intentBreakdownData.reduce((s, d) => s + d.value, 0)

  // Only stack series that actually occur in the period — an eight-series
  // stacked area with five empty bands is unreadable.
  const intentSeries = data.intent_over_time ?? []
  const activeIntents = INTENT_ORDER.filter(
    (k) => k !== 'unanalyzed' && intentSeries.some((row) => (row as Record<string, number>)[k] > 0),
  )

  return (
    // Dimmed while the next scope loads, so a filter change reads as "this is
    // updating" rather than as new numbers that happen to look identical.
    <div className={cn('space-y-5', isPreviousData && 'opacity-60')} aria-busy={isPreviousData || undefined}>
      {/* Row 1: KPI Cards.
          Reply count and reply rate are two views of ONE metric, so they share
          the `replied` violet on purpose (§61 — one hue per metric, everywhere).
          "Positive rate" borrows the sentiment scale because that is what it
          measures, and "Avg response time" has no lifecycle series of its own,
          so it takes the brand indigo `opened` sits on — deliberately not amber. */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Total replies"     metric={data.kpis.total_replies}           format="number"  color={seriesColor('replies')} />
        <KpiCard label="Reply rate"        metric={data.kpis.reply_rate}              format="percent" color={seriesColor('replies')} />
        <KpiCard label="Positive rate"     metric={data.kpis.positive_rate}           format="percent" color={sentimentChartColor('positive')} />
        {/* Lower is better: a slower average reply is bad news, so the delta pill
            must not paint an increase green. */}
        <KpiCard label="Avg response time" metric={data.kpis.avg_response_time_hours} format="hours"   invertColor color={SERIES_COLORS.opened} />
      </div>

      {/* Row 2: Sentiment over time + Sentiment breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className={cardCls}>
          <CardHeader>
            <CardTitle className="text-foreground dark:text-[#f7f8f8]" style={cardTitleStyle}>
              Sentiment over time
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.sentiment_over_time.length > 0 ? (
              // The chart is keyboard-interactive, so its name goes on a
              // role="group" wrapper: role="img" would hide the data points
              // recharts' accessibilityLayer exposes on the SVG itself, and a
              // bare <div> has no role that supports being named.
              <div role="group" aria-label="Reply sentiment over time — stacked area chart, one band per sentiment">
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart accessibilityLayer data={data.sentiment_over_time} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <defs>
                      {SENTIMENT_SERIES.map((s) => (
                        <linearGradient key={s.key} id={s.gradientId} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor={s.color} stopOpacity={0.4} />
                          <stop offset="95%" stopColor={s.color} stopOpacity={0} />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: chartTheme.tickFill }} stroke={chartTheme.axis} strokeWidth={1} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: chartTheme.tickFill }} stroke={chartTheme.axis} strokeWidth={1} tickLine={false} axisLine={false} />
                    <Tooltip
                      cursor={{ stroke: chartTheme.axis, strokeDasharray: '3 3' }}
                      contentStyle={tooltipContentStyle(chartTheme)}
                      labelStyle={{ color: chartTheme.tooltipText, fontWeight: 590, marginBottom: 4 }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px', paddingTop: 8 }} iconType="circle" iconSize={8} />
                    {/* One <Area> per shared series — hue, label, stacking order and
                        the negative-valence dash are all derived, never literal. */}
                    {SENTIMENT_SERIES.map((s) => (
                      <Area
                        key={s.key}
                        type="monotone"
                        dataKey={s.key}
                        stackId="1"
                        name={s.label}
                        stroke={s.color}
                        strokeWidth={2}
                        strokeDasharray={s.dash}
                        fill={`url(#${s.gradientId})`}
                        isAnimationActive={!prefersReduced}
                      />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <EmptyChart message="No sentiment data yet." icon={<ChatCircle className="h-5 w-5" weight="regular" />} />
            )}
          </CardContent>
        </Card>

        <Card className={cardCls}>
          <CardHeader>
            <CardTitle className="text-foreground dark:text-[#f7f8f8]" style={cardTitleStyle}>
              Sentiment breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sentimentBreakdownTotal > 0 ? (
              <div className="flex flex-col items-center">
                {/* w-full is load-bearing: the parent centres its cross axis, so an
                    auto-width wrapper would shrink to its content and the
                    container's width="100%" would resolve against zero. */}
                <div role="group" aria-label="Reply sentiment breakdown — donut chart of replies by sentiment" className="w-full">
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart accessibilityLayer>
                      <Pie
                        data={sentimentSlices}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={85}
                        paddingAngle={2}
                        dataKey="value"
                        isAnimationActive={!prefersReduced}
                      >
                        {sentimentSlices.map((entry) => (
                          <Cell key={entry.key} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={tooltipContentStyle(chartTheme)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-2.5 mt-3 justify-center">
                  {sentimentBreakdownData.map((d) => (
                    <div
                      key={d.key}
                      className="inline-flex items-center gap-1.5 text-muted-foreground dark:text-[#8a8f98]"
                      style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
                    >
                      <span
                        className="h-2 w-2 rounded-full"
                        style={
                          d.value > 0
                            ? { backgroundColor: d.color }
                            : { boxShadow: `inset 0 0 0 1px ${d.color}`, opacity: 0.5 }
                        }
                      />
                      <span className={d.value > 0 ? undefined : 'opacity-60'}>{d.name}</span>
                      <span className="opacity-60 tabular-nums">
                        {sentimentBreakdownTotal > 0
                          ? Math.round((d.value / sentimentBreakdownTotal) * 100)
                          : 0}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <EmptyChart message="No sentiment data yet." icon={<ChatCircle className="h-5 w-5" weight="regular" />} />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Intent over time + Intent breakdown — the same two shapes as
          the sentiment row above, on the independent "what they want" axis. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className={cardCls}>
          <CardHeader>
            <CardTitle className="text-foreground dark:text-[#f7f8f8]" style={cardTitleStyle}>
              Intent over time
            </CardTitle>
          </CardHeader>
          <CardContent>
            {intentSeries.length > 0 && activeIntents.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={intentSeries} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: chartTheme.tickFill }} stroke={chartTheme.axis} strokeWidth={1} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: chartTheme.tickFill }} stroke={chartTheme.axis} strokeWidth={1} tickLine={false} axisLine={false} />
                  <Tooltip
                    cursor={{ stroke: chartTheme.axis, strokeDasharray: '3 3' }}
                    contentStyle={tooltipContentStyle(chartTheme)}
                    labelStyle={{ color: chartTheme.tooltipText, fontWeight: 590, marginBottom: 4 }}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px', paddingTop: 8 }} iconType="circle" iconSize={8} />
                  {activeIntents.map((key) => (
                    <Area
                      key={key}
                      type="monotone"
                      dataKey={key}
                      name={INTENT_LABELS[key] ?? key}
                      stackId="1"
                      stroke={INTENT_COLORS[key]}
                      fill={INTENT_COLORS[key]}
                      fillOpacity={0.22}
                      strokeWidth={2}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart message="No intent data yet." />
            )}
          </CardContent>
        </Card>

        <Card className={cardCls}>
          <CardHeader>
            <CardTitle className="text-foreground dark:text-[#f7f8f8]" style={cardTitleStyle}>
              Intent breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {intentBreakdownTotal > 0 ? (
              <div className="flex flex-col items-center">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={intentBreakdownData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={85}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {intentBreakdownData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipContentStyle(chartTheme)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-2.5 mt-3 justify-center">
                  {intentBreakdownData.map((d) => (
                    <div
                      key={d.name}
                      className="inline-flex items-center gap-1.5 text-muted-foreground dark:text-[#8a8f98]"
                      style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
                    >
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: d.color }} />
                      <span>{d.name}</span>
                      <span className="opacity-60 tabular-nums">
                        {Math.round((d.value / intentBreakdownTotal) * 100)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <EmptyChart message="No intent data yet." />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 4: Top Campaigns + Recent Replies */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className={cardCls}>
          <CardHeader>
            <CardTitle className="text-foreground dark:text-[#f7f8f8]" style={cardTitleStyle}>
              Top campaigns by reply rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.top_campaigns_by_reply.length > 0 ? (
              <div className="space-y-1.5">
                {data.top_campaigns_by_reply.map((c, i) => (
                  <div
                    key={c.id}
                    className="flex items-center gap-2.5 rounded-md px-2.5 py-2 hover:bg-muted/40 dark:hover:bg-white/[0.04] transition-colors duration-150"
                  >
                    <span
                      className={cn(
                        'inline-flex items-center justify-center h-6 w-6 rounded-full border flex-shrink-0 tabular-nums',
                        'bg-[#5e6ad2]/10 text-[#5e6ad2] border-[#5e6ad2]/20',
                        'dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/25',
                      )}
                      style={{ fontSize: '11px', fontWeight: 590, letterSpacing: '-0.1px' }}
                    >
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div
                        className="text-foreground dark:text-[#f7f8f8] truncate"
                        style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                      >
                        {c.name}
                      </div>
                      <div
                        className="text-muted-foreground dark:text-[#8a8f98] tabular-nums"
                        style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
                      >
                        {c.sent.toLocaleString()} sent · {c.replies.toLocaleString()} replies
                      </div>
                    </div>
                    <span
                      className={cn(
                        'whitespace-nowrap inline-flex items-center px-2 py-0.5 rounded-full border tabular-nums',
                        'bg-[#5e6ad2]/10 text-[#5e6ad2] border-[#5e6ad2]/25',
                        'dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/30',
                      )}
                      style={{ fontSize: '11px', fontWeight: 590, letterSpacing: '-0.05px' }}
                    >
                      {c.reply_rate.toFixed(1)}%
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyChart message="No campaign reply data yet." icon={<ChatCircle className="h-5 w-5" weight="regular" />} />
            )}
          </CardContent>
        </Card>

        <Card className={cardCls}>
          <CardHeader>
            <CardTitle className="text-foreground dark:text-[#f7f8f8]" style={cardTitleStyle}>
              Recent replies
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.recent_replies.length > 0 ? (
              <div className="space-y-2.5 max-h-[400px] overflow-y-auto pr-1">
                {data.recent_replies.map((reply, i) => {
                  // The shared map covers every case this file used to miss —
                  // interested / not_interested / out_of_office fell through to a
                  // muted chip, and a NULL sentiment rendered an empty pill
                  // instead of an honest "Unanalyzed".
                  const sentiment = sentimentMeta(reply.sentiment)
                  const intent = reply.intent ? intentLabel(reply.intent) : ''
                  // An unparseable timestamp used to reach the user as the literal
                  // string "Invalid Date"; drop the line rather than print a lie.
                  const repliedAt = reply.timestamp ? new Date(reply.timestamp) : null
                  const repliedOn =
                    repliedAt && !Number.isNaN(repliedAt.getTime()) ? repliedAt.toLocaleDateString() : ''
                  return (
                    <div
                      key={i}
                      className={cn(
                        'rounded-lg border p-3 space-y-2 transition-colors duration-150',
                        'bg-card border-border/60 hover:bg-muted/40',
                        'dark:bg-white/[0.02] dark:border-white/[0.08] dark:hover:bg-white/[0.04]',
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className="text-foreground dark:text-[#f7f8f8] truncate"
                          style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}
                        >
                          {reply.contact_name}
                        </span>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <span
                            className={cn(
                              'inline-flex items-center px-2 py-0.5 rounded-full border',
                              sentiment.borderChipClass,
                            )}
                            style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.04em' }}
                          >
                            {sentiment.label}
                          </span>
                          {intent && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full border bg-muted/60 text-muted-foreground border-border/50 dark:bg-white/[0.04] dark:text-[#8a8f98] dark:border-white/[0.06]"
                              style={{ fontSize: '10px', fontWeight: 510, letterSpacing: '0.04em' }}
                            >
                              {intent}
                            </span>
                          )}
                        </div>
                      </div>
                      <p
                        className="text-muted-foreground dark:text-[#8a8f98] line-clamp-2 leading-relaxed"
                        style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                      >
                        {reply.preview}
                      </p>
                      <div
                        className="flex items-center justify-between text-muted-foreground dark:text-[#8a8f98] tabular-nums"
                        style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
                      >
                        {repliedOn && <span>{repliedOn}</span>}
                        {/* ml-auto, not ml-2: with no date to sit opposite it,
                            justify-between left the campaign name stranded on the left. */}
                        {reply.campaign_name && <span className="truncate ml-auto pl-2 opacity-70">{reply.campaign_name}</span>}
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <EmptyChart message="No recent replies yet." icon={<ChatCircle className="h-5 w-5" weight="regular" />} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
